import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { finalize, Subject, takeUntil } from 'rxjs';
import { FormVersionDto } from 'src/core/models/quality-management/form-version.dto';
import { FormVersionSelectionModel } from 'src/core/models/quality-management/form-version-selection.model';
import { FormVersionSelectorService } from '../../services/form-version-selector.service';

@Component({
  selector: 'ca-form-version-selector',
  templateUrl: './form-version-selector.component.html',
  styleUrls: ['./form-version-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormVersionSelectorComponent),
    },
  ],
  standalone: false,
})
export class FormVersionSelectorComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @ViewChild(NgSelectComponent)
  private versionSelect!: NgSelectComponent;

  @Input()
  formId: number | null | undefined = null;

  versions: FormVersionDto[] = [];
  selectedVersion: FormVersionDto | null = null;
  useAlwaysLatest: boolean = true;
  loading: boolean = false;
  isDisabled: boolean = false;

  private initialized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: FormVersionSelectorService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formId && this.initialized) {
      this.resetVersions();
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: FormVersionSelectionModel | null): void {
    if (value) {
      this.useAlwaysLatest = value.useAlwaysLatest;
    }

    this.selectedVersion = value?.formVersion ?? null;

    if (this.useAlwaysLatest) {
      this.selectedVersion = this.latestVersion;
    }
  }

  registerOnChange(fn: (value: FormVersionSelectionModel | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onUseAlwaysLatestChange(checked: boolean): void {
    this.useAlwaysLatest = checked;

    if (checked) {
      this.selectedVersion = this.latestVersion;
    }

    this.emitValue();
  }

  onVersionChange(version: FormVersionDto | null): void {
    this.selectedVersion = version;
    this.emitValue();
  }

  onClearSelectionClick(eventArgs: MouseEvent): void {
    eventArgs.preventDefault();
    eventArgs.stopPropagation();

    this.useAlwaysLatest = false;
    this.selectedVersion = null;
    this.emitValue();
  }

  onDropdownOpen(): void {
    setTimeout(() => {
      const dropdownId = this.versionSelect?.dropdownId;
      const dropdownEl = dropdownId ? document.getElementById(dropdownId) : null;
      const hostEl = this.elementRef.nativeElement as HTMLElement;
      const selectEl = hostEl.querySelector('ng-select') as HTMLElement;

      if (!dropdownEl || !selectEl) {
        return;
      }

      const width = selectEl.getBoundingClientRect().width;

      dropdownEl.style.setProperty('width', `${width}px`, 'important');
      dropdownEl.style.setProperty('min-width', `${width}px`, 'important');
      dropdownEl.style.setProperty('max-width', `${width}px`, 'important');
    });
  }

  onChange(value: FormVersionSelectionModel | null): void {}

  onTouched(): void {}

  get hasForm(): boolean {
    return this.formId != null;
  }

  get dropdownDisabled(): boolean {
    return this.isDisabled || this.useAlwaysLatest || !this.hasForm || this.loading;
  }

  get latestVersion(): FormVersionDto | null {
    return this.versions[0] ?? null;
  }

  get hasSelection(): boolean {
    return this.selectedVersion != null;
  }

  private load(): void {
    if (!this.hasForm) {
      this.versions = [];
      this.selectedVersion = null;
      this.emitValue();
      return;
    }

    const formId = this.formId as number;

    this.loading = true;

    this.service
      .getVersions(formId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(versions => {
        if (this.formId !== formId) {
          return;
        }

        this.versions = versions;

        if (this.useAlwaysLatest) {
          this.selectedVersion = this.latestVersion;
        } else if (this.versions.length === 1) {
          this.selectedVersion = this.versions[0];
        } else if (this.selectedVersion) {
          this.selectedVersion =
            this.versions.find(version => version.id === this.selectedVersion.id) ?? null;
        }

        this.emitValue();
      });
  }

  private resetVersions(): void {
    this.versions = [];
    this.selectedVersion = null;
  }

  private emitValue(): void {
    if (!this.selectedVersion) {
      this.onChange(null);
      return;
    }

    this.onChange({
      formVersion: this.selectedVersion,
      useAlwaysLatest: this.useAlwaysLatest,
    });
  }
}
